import React, { useState, useRef, useEffect } from 'react'
import { Send, RefreshCw, User, Loader2 } from 'lucide-react'
import { AiChatPageProps } from '../model'
import { cn } from '../../../../_shared/lib'
import { GuideQuestions } from './organisms/GuideQuestions'
import { getRandomGuideQuestions } from './organisms/GuideQuestions'
import { Button } from '../../../molecules/button'
import { Textarea } from '../../../molecules/textarea'
import { partitionAiChatEventsByUserMessage, AiChatEventsPartitionedByUserMessage } from './organisms/AiChatEventsPartitionedByUserMessage'

export const AiChatPagePresentation: React.FC<AiChatPageProps> = ({
  currentAiChatSession,
  onStartNewAiChatSession,
  onSendUserMessageAiChatEvent,
  isSendingMessage,
  textFieldRef,
}) => {
  const [inputValue, setInputValue] = useState('')
  const [isComposing, setIsComposing] = useState(false)

  // ガイド質問関連のローカル状態
  const [isTextFieldFocused, setIsTextFieldFocused] = useState(false)
  const [guideQuestions, setGuideQuestions] = useState<string[]>([])

  // SourceDocument展開状態
  const [isAnySourceDocumentExpanded, setIsAnySourceDocumentExpanded] = useState(false)

  // スクロールコンテナへの参照
  const messagesContainerRef = useRef<HTMLDivElement>(null)

  const handleCompositionStart = () => setIsComposing(true)
  const handleCompositionEnd = () => setIsComposing(false)

  // TextFieldフォーカス関連のハンドラー
  const handleTextFieldFocus = () => {
    setIsTextFieldFocused(true)
    // フォーカス時にガイド質問を生成（セッションがない、またはイベントがない場合のみ）
    const hasEvents = currentAiChatSession?.aiChatEvents && currentAiChatSession.aiChatEvents.length > 0
    if (!currentAiChatSession || !hasEvents) {
      setGuideQuestions(getRandomGuideQuestions(4))
    }
  }

  const handleTextFieldBlur = () => {
    // 少し遅延してから非表示にする（クリックイベントを先に処理するため）
    setTimeout(() => {
      setIsTextFieldFocused(false)
    }, 150)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (inputValue.trim()) {
      onSendUserMessageAiChatEvent(inputValue.trim())
      setInputValue('')
    }
  }

  // 質問がクリックされたときに入力値を更新
  const handleQuestionClick = (question: string) => {
    setInputValue(question)
    setIsTextFieldFocused(false) // ガイドを非表示
    // フォーカスをTextFieldに戻す
    if (textFieldRef.current) {
      textFieldRef.current.focus()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !isComposing) {
      e.preventDefault()
      if (inputValue.trim()) {
        onSendUserMessageAiChatEvent(inputValue.trim())
        setInputValue('')
      }
    }
  }

  const aiChatEvents = currentAiChatSession?.aiChatEvents || []

  // userMessageが追加された時に最下部へスクロール
  useEffect(() => {
    if (aiChatEvents.length === 0) return

    const lastEvent = aiChatEvents[aiChatEvents.length - 1]
    if (lastEvent && lastEvent.payload.type === 'userMessage' && messagesContainerRef.current) {
      // スムーズにスクロール
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth',
      })
    }
  }, [aiChatEvents])

  // useMemoを使って、AiChatEventsPartitionedByUserMessagePropsの配列を生成
  const partitionedEvents = React.useMemo(() => {
    return partitionAiChatEventsByUserMessage(aiChatEvents, isSendingMessage)
  }, [aiChatEvents, isSendingMessage])

  return (
    <div className="flex flex-row w-full h-full overflow-hidden bg-white">
      {/* Left: Chat Area */}
      <div className="flex-1 h-full flex flex-col">
        {/* Header */}
        <header className="border-b border-gray-200 bg-white">
          <div className="flex items-center min-h-[56px] px-4">
            <h1 className="flex-grow text-base font-semibold">IRDB AI Chat</h1>
            {currentAiChatSession && (
              <Button variant="outline" onClick={onStartNewAiChatSession} disabled={isSendingMessage} size="sm">
                <RefreshCw className="mr-2 h-4 w-4" />
                新しいチャットを始める
              </Button>
            )}
          </div>
        </header>

        {/* Messages */}
        <div ref={messagesContainerRef} className={cn('flex-1 self-center flex flex-col items-center gap-3 overflow-y-auto', 'w-full')}>
          {aiChatEvents.length === 0 && !currentAiChatSession ? (
            <div className="flex flex-col items-center p-16 gap-4 text-center text-gray-500">
              <User className="h-10 w-10 text-blue-600 mb-2" />
              <span className="text-xs text-gray-400">質問を入力してチャットを開始してください</span>
            </div>
          ) : (
            <div className="pt-4 flex flex-col gap-4 w-full">
              {partitionedEvents.map((partition) => (
                <AiChatEventsPartitionedByUserMessage
                  key={partition.userMessageEvent.id}
                  {...partition}
                  isAnySourceDocumentExpanded={isAnySourceDocumentExpanded}
                  onSourceDocumentExpandedChange={setIsAnySourceDocumentExpanded}
                />
              ))}
            </div>
          )}

          {isSendingMessage && (
            <div className="self-start w-full">
              <div className="flex items-center gap-2 p-3 bg-gray-100 rounded-lg">
                <span className="text-sm">考え中</span>
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            </div>
          )}
        </div>

        <div className={cn('flex items-center', 'w-[768px]')}>
          {/* Guide Questions */}
          <GuideQuestions
            questions={guideQuestions}
            onQuestionClick={handleQuestionClick}
            show={
              isTextFieldFocused &&
              guideQuestions.length > 0 &&
              (!currentAiChatSession || !currentAiChatSession.aiChatEvents || currentAiChatSession.aiChatEvents.length === 0)
            }
          />
        </div>

        {/* Input */}
        <form onSubmit={handleSubmit} className="relative p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-center items-center gap-2">
            <div className={cn('flex flex-row gap-2', 'w-[768px]')}>
              <Textarea
                className="flex-1 rounded-3xl resize-none min-h-[40px] max-h-[160px]"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                onCompositionStart={handleCompositionStart}
                onCompositionEnd={handleCompositionEnd}
                onFocus={handleTextFieldFocus}
                onBlur={handleTextFieldBlur}
                placeholder="メッセージを入力してください..."
                disabled={isSendingMessage}
                ref={textFieldRef as React.RefObject<HTMLTextAreaElement>}
              />
              <Button
                type="submit"
                disabled={!inputValue.trim() || isSendingMessage}
                className={cn(
                  'rounded-full p-3 h-auto',
                  inputValue.trim() && !isSendingMessage ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-gray-300 hover:bg-gray-400 text-white',
                )}
                title="送信"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
